import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ExternalLink, RefreshCw, Search } from "lucide-react"

import {
  useCreateKlowOffer,
  useKlowArticles,
  useKlowArticle,
  useKlowGenerateArticle,
  useKlowGenerationStatus,
  useKlowGoogleTemplates,
  useKlowPixels,
  useKlowRsocTemplates,
  useSyncKlowArticles,
  type KlowArticle,
  type VerticalOption,
} from "@/api/hooks/klow"
import { ApiError, apiFetch } from "@/api/client"
import { useAuth } from "@/auth/AuthProvider"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  DEFAULT_KLOW_LANGUAGE,
  KLOW_LANGUAGES,
  type KlowLanguageCode,
} from "@/lib/klowLanguages"
import { buildKlowArticleBaseUrl, buildKlowFeedUrl, firstSuggestedHeadline, isKlowArticleReady } from "@/lib/klowFeedUrl"
import { cn } from "@/lib/utils"

type ArticleMode = "existing" | "generate"

type KlowValidationField =
  | "offerName"
  | "article"
  | "topic"
  | "language"
  | "rsocTemplateId"
  | "googleTemplateId"
  | "pixelId"

function klowFieldInputClass(hasError: boolean) {
  return cn(
    hasError
      ? "border-red-500 bg-[#3a2222] text-[#fecaca] focus-visible:ring-red-500"
      : "border-[#555] bg-[#3a3a3a]",
  )
}

function klowFieldLabelClass(hasError: boolean) {
  return cn(hasError && "text-red-400")
}

interface KlowOfferModalProps {
  open: boolean
  onClose: () => void
  verticalOptions: VerticalOption[]
  isLoadingVerticals?: boolean
  onSuccess: (offer: { target_url: string | null; name: string }) => void
}

export function KlowOfferModal({
  open,
  onClose,
  verticalOptions,
  isLoadingVerticals = false,
  onSuccess,
}: KlowOfferModalProps) {
  const [offerName, setOfferName] = useState("")
  const [verticalSlug, setVerticalSlug] = useState("")
  const [mode, setMode] = useState<ArticleMode>("existing")
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null)
  const [previewArticle, setPreviewArticle] = useState<KlowArticle | null>(null)

  const [topic, setTopic] = useState("")
  const [title, setTitle] = useState("")
  const [keywords, setKeywords] = useState("")
  const [additional, setAdditional] = useState("")
  const [language, setLanguage] = useState<KlowLanguageCode>(DEFAULT_KLOW_LANGUAGE)
  const [rsocTemplateId, setRsocTemplateId] = useState("")
  const [googleTemplateId, setGoogleTemplateId] = useState("")
  const [adCopyText, setAdCopyText] = useState("")
  const [sqsKeywords, setSqsKeywords] = useState("")
  const [pixelId, setPixelId] = useState("")

  const [pollingJobId, setPollingJobId] = useState<number | null>(null)
  const [pollingRequestId, setPollingRequestId] = useState<number | null>(null)
  const [pollingArticleId, setPollingArticleId] = useState<number | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorField, setErrorField] = useState<KlowValidationField | null>(null)
  const fieldRefs = useRef<Partial<Record<KlowValidationField, HTMLDivElement | null>>>({})
  const [articleSearch, setArticleSearch] = useState("")
  const [showAllArticles, setShowAllArticles] = useState(false)
  const [debouncedArticleSearch, setDebouncedArticleSearch] = useState("")
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [prefilledArticleId, setPrefilledArticleId] = useState<number | null>(null)

  const clearValidation = useCallback(() => {
    setError(null)
    setErrorField(null)
  }, [])

  const showFieldError = useCallback(
    (field: KlowValidationField, message: string) => {
      setErrorField(field)
      setError(message)
    },
    [],
  )

  const clearFieldError = useCallback(
    (field: KlowValidationField) => {
      if (errorField === field) {
        clearValidation()
      }
    },
    [clearValidation, errorField],
  )

  useEffect(() => {
    if (!errorField) return
    const target = fieldRefs.current[errorField]
    if (!target) return
    const frame = window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "center" })
      const focusable = target.querySelector<HTMLElement>(
        "input, textarea, button[role='combobox']",
      )
      focusable?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [errorField, error])

  const clearExistingArticleSelection = useCallback(() => {
    setPrefilledArticleId(null)
    setSelectedArticleId(null)
    setPreviewArticle(null)
    setAdCopyText("")
    setSqsKeywords("")
    setPixelId("")
  }, [])

  const handleModeChange = useCallback(
    (next: ArticleMode) => {
      if (next === mode) return
      if (next === "generate") {
        clearExistingArticleSelection()
        clearValidation()
      }
      setMode(next)
    },
    [mode, clearExistingArticleSelection, clearValidation],
  )

  const handleDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) return
      onClose()
    },
    [onClose],
  )

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedArticleSearch(articleSearch.trim())
    }, 300)
    return () => window.clearTimeout(timer)
  }, [articleSearch])

  const { user, isAdmin } = useAuth()
  const articlesQuery = useKlowArticles({
    enabled: open,
    search: debouncedArticleSearch,
    mine: !showAllArticles,
  })
  const syncArticlesMutation = useSyncKlowArticles()
  const articleDetailQuery = useKlowArticle(selectedArticleId, open && selectedArticleId != null)
  const rsocTemplatesQuery = useKlowRsocTemplates(open)
  const googleTemplatesQuery = useKlowGoogleTemplates(open)
  const pixelsQuery = useKlowPixels({ enabled: open, userId: user?.id, mine: true })

  const refetchPixels = pixelsQuery.refetch
  useEffect(() => {
    if (!open || user?.id == null) return
    void refetchPixels()
  }, [open, user?.id, refetchPixels])

  const generateMutation = useKlowGenerateArticle()
  const statusMutation = useKlowGenerationStatus()
  const statusMutateRef = useRef(statusMutation.mutateAsync)
  const pollInFlightRef = useRef(false)
  const articlesRefetchRef = useRef(articlesQuery.refetch)
  useEffect(() => {
    statusMutateRef.current = statusMutation.mutateAsync
    articlesRefetchRef.current = articlesQuery.refetch
  })
  const createOfferMutation = useCreateKlowOffer()

  const articles = useMemo(
    () => articlesQuery.data?.data ?? [],
    [articlesQuery.data?.data],
  )
  const readyArticles = useMemo(
    () => articles.filter(isKlowArticleReady),
    [articles],
  )

  const listArticles = useMemo(() => {
    const seen = new Set<number>()
    const combined: KlowArticle[] = []
    const add = (item: KlowArticle) => {
      if (seen.has(item.id)) return
      seen.add(item.id)
      combined.push(item)
    }
    for (const item of articles) {
      if (item.generation_status === "IN PROGRESS") add(item)
    }
    for (const item of readyArticles) add(item)
    return combined
  }, [articles, readyArticles])

  const displayPreview =
    articleDetailQuery.data?.data ?? previewArticle

  const articleBaseUrl = useMemo(() => {
    if (!displayPreview) return ""
    return (
      displayPreview.base_url ||
      buildKlowArticleBaseUrl(
        displayPreview.domain,
        displayPreview.slug,
        displayPreview.url,
      )
    )
  }, [displayPreview])

  const previewFeedUrl = useMemo(() => {
    if (!articleBaseUrl) return ""
    return buildKlowFeedUrl(articleBaseUrl, {
      rsocTemplateId: rsocTemplateId || undefined,
      adCopyText: adCopyText,
      suggestedKeywords: sqsKeywords.trim() || undefined,
      googleTemplateId: googleTemplateId || undefined,
      pixelId: pixelId || undefined,
      publisherTracking1: displayPreview?.publisher_tracking_1 ?? undefined,
      publisherTracking2: displayPreview?.publisher_tracking_2 ?? undefined,
      publisherTracking3: displayPreview?.publisher_tracking_3 ?? undefined,
    })
  }, [
    articleBaseUrl,
    displayPreview,
    rsocTemplateId,
    adCopyText,
    sqsKeywords,
    googleTemplateId,
    pixelId,
  ])

  // Prefill feed params once per selected article (adjusting state during
  // render instead of in an effect).
  if (!selectedArticleId) {
    if (prefilledArticleId !== null) setPrefilledArticleId(null)
  } else if (prefilledArticleId !== selectedArticleId) {
    const article =
      readyArticles.find((a) => a.id === selectedArticleId) ?? previewArticle
    if (article && article.id === selectedArticleId) {
      setPrefilledArticleId(selectedArticleId)
      setAdCopyText(
        article.ad_copy_text ||
          firstSuggestedHeadline(article.suggested_ad_headlines) ||
          article.title ||
          "",
      )
      setRsocTemplateId(article.rsoc_template_id || "")
      setGoogleTemplateId(article.google_template_id || "")
      setSqsKeywords(article.keywords || "")
      setPixelId(article.pixel_id || "")
    }
  }

  const resetForm = () => {
    setPrefilledArticleId(null)
    setOfferName("")
    setVerticalSlug("")
    setMode("existing")
    setSelectedArticleId(null)
    setPreviewArticle(null)
    setTopic("")
    setTitle("")
    setKeywords("")
    setAdditional("")
    setLanguage(DEFAULT_KLOW_LANGUAGE)
    setRsocTemplateId("")
    setGoogleTemplateId("")
    setAdCopyText("")
    setSqsKeywords("")
    setPixelId("")
    setPollingJobId(null)
    setPollingRequestId(null)
    setPollingArticleId(null)
    setStatusMessage(null)
    clearValidation()
    setArticleSearch("")
    setDebouncedArticleSearch("")
    setShowAllArticles(false)
    setSyncMessage(null)
  }

  // Reset the form when the modal closes.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (!open) resetForm()
  }

  const isArticleReady = useCallback(
    (article: KlowArticle) => isKlowArticleReady(article),
    [],
  )

  const completePendingArticle = useCallback(async (article: KlowArticle) => {
    setSelectedArticleId(article.id)
    setPreviewArticle(article)
    setPollingJobId(null)
    setPollingRequestId(null)
    setPollingArticleId(null)
    setStatusMessage("Article ready — create your offer below.")
    setError(null)
    setMode("existing")
    await articlesRefetchRef.current()
  }, [])

  const tryRecoverPendingArticle = useCallback(async () => {
    if (!pollingArticleId) return false
    try {
      const result = await apiFetch<{ success: boolean; data: KlowArticle }>(
        `/api/klow/articles/${pollingArticleId}`,
      )
      if (isArticleReady(result.data)) {
        await completePendingArticle(result.data)
        return true
      }
    } catch {
      // Local read failed — keep polling RSOC status.
    }
    return false
  }, [pollingArticleId, isArticleReady, completePendingArticle])

  const pollGeneration = useCallback(async () => {
    if (!pollingJobId && !pollingRequestId) return
    if (pollInFlightRef.current) return
    pollInFlightRef.current = true
    try {
      const result = await statusMutateRef.current({
        job_id: pollingJobId ?? undefined,
        request_id: pollingRequestId ?? undefined,
      })
      const payload = result.data
      const status = String(payload?.status ?? "")
      const article = result.article
      const articleReady = Boolean(article && isArticleReady(article))
      if (articleReady && article) {
        await completePendingArticle(article)
        return
      }
      if (status === "IN PROGRESS" || !articleReady) {
        setError(null)
        setStatusMessage(
          "Generating article… usually ready in 3–5 minutes. We'll fetch the slug automatically.",
        )
        return
      }
      if (payload?.error) {
        setError(String(payload.error))
        setPollingJobId(null)
        setPollingRequestId(null)
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 0) {
        if (await tryRecoverPendingArticle()) return
        setError(null)
        setStatusMessage(
          "Still generating on KLOW — we'll keep checking. Generation continues even if status checks hiccup; switch to Use existing and sync if needed.",
        )
        return
      }
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Status check failed"
      setError(msg)
    } finally {
      pollInFlightRef.current = false
    }
  }, [
    pollingJobId,
    pollingRequestId,
    isArticleReady,
    completePendingArticle,
    tryRecoverPendingArticle,
  ])

  useEffect(() => {
    if (!pollingJobId && !pollingRequestId) return
    const id = window.setInterval(() => {
      void pollGeneration()
    }, 30000)
    void pollGeneration()
    return () => window.clearInterval(id)
  }, [pollingJobId, pollingRequestId, pollGeneration])

  const handleStartGenerate = async () => {
    clearValidation()
    if (!topic.trim()) {
      showFieldError("topic", "Topic is required")
      return
    }
    if (!language) {
      showFieldError("language", "Language is required")
      return
    }
    try {
      const result = await generateMutation.mutateAsync({
        topic: topic.trim(),
        title: title.trim() || undefined,
        keywords: keywords.trim() || undefined,
        additional: additional.trim() || undefined,
        language,
      })
      setPollingJobId(result.data.job_id ?? null)
      setPollingRequestId(result.data.request_id ?? null)
      setPollingArticleId(result.data.article?.id ?? null)
      setStatusMessage(
        "Article generation started… usually ready in 3–5 minutes.",
      )
      void articlesRefetchRef.current()
    } catch (err) {
      clearValidation()
      setError(err instanceof ApiError ? err.message : "Generation failed")
    }
  }

  const handleCreateOffer = async () => {
    clearValidation()
    if (!offerName.trim()) {
      showFieldError("offerName", "Offer name is required")
      return
    }
    if (!selectedArticleId) {
      setMode("existing")
      showFieldError("article", "Select a ready article first")
      return
    }
    if (!rsocTemplateId.trim()) {
      showFieldError("rsocTemplateId", "Article template (ct) is required")
      return
    }
    if (!googleTemplateId.trim()) {
      showFieldError("googleTemplateId", "KW template (stid) is required")
      return
    }
    if (!pixelId.trim()) {
      showFieldError("pixelId", "Pixel (spid) is required")
      return
    }
    const vertical = verticalOptions.find((v) => v.slug === verticalSlug)
    try {
      const result = await createOfferMutation.mutateAsync({
        name: offerName.trim(),
        klow_article_id: selectedArticleId,
        vertical_slug: vertical?.slug,
        vertical_name: vertical?.name,
        rsoc_template_id: rsocTemplateId.trim(),
        google_template_id: googleTemplateId.trim(),
        ad_copy_text: adCopyText.trim(),
        suggested_keywords: sqsKeywords.trim() || undefined,
        pixel_id: pixelId.trim(),
        traffic_source: "TT",
      })
      onSuccess({
        target_url: result.data.target_url,
        name: result.data.name,
      })
      onClose()
    } catch (err) {
      clearValidation()
      setError(err instanceof ApiError ? err.message : "Failed to create offer")
    }
  }

  const isBusy =
    isLoadingVerticals ||
    generateMutation.isPending ||
    createOfferMutation.isPending ||
    (Boolean(pollingJobId || pollingRequestId) &&
      !(
        selectedArticleId &&
        readyArticles.some((a) => a.id === selectedArticleId)
      ))

  const canCreateOffer =
    Boolean(selectedArticleId) &&
    Boolean(offerName.trim()) &&
    Boolean(rsocTemplateId.trim()) &&
    Boolean(googleTemplateId.trim()) &&
    Boolean(pixelId.trim())

  const pixelSelectOptions = useMemo(() => {
    const raw = pixelsQuery.data?.data
    if (!Array.isArray(raw)) return []
    return raw.map((p) => ({
      value: String(p.rsoc_spid),
      label: p.label || `${p.name} (${p.rsoc_spid})`,
    }))
  }, [pixelsQuery.data?.data])

  const pixelsLoadErrorMessage = useMemo(() => {
    const err = pixelsQuery.error
    if (!err) return null
    if (err instanceof ApiError) return err.message
    if (err instanceof Error) return err.message
    return "Could not load your assigned pixels."
  }, [pixelsQuery.error])

  const rsocTemplateOptions = useMemo(
    () =>
      (rsocTemplatesQuery.data?.data ?? []).map((t) => ({
        value: String(t.ct),
        label: t.NAME ? `${t.NAME} (${t.ct})` : String(t.ct),
      })),
    [rsocTemplatesQuery.data?.data],
  )

  const googleTemplateOptions = useMemo(
    () =>
      (googleTemplatesQuery.data?.data ?? []).map((t) => ({
        value: String(t.style_id),
        label: `Style ${t.style_id}`,
      })),
    [googleTemplatesQuery.data?.data],
  )

  const renderFieldError = (field: KlowValidationField) =>
    errorField === field && error ? (
      <p className="mt-1 text-xs text-red-400">{error}</p>
    ) : null

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-[#4a4a4a] bg-[#2a2a2a] text-[#e0e0e0]">
        <DialogHeader>
          <DialogTitle>Create KLOW link</DialogTitle>
        </DialogHeader>

        {isLoadingVerticals ? (
          <p className="py-8 text-center text-sm text-[#888]">Loading verticals…</p>
        ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div
              ref={(el) => {
                fieldRefs.current.offerName = el
              }}
            >
              <Label
                htmlFor="klow-offer-name"
                className={klowFieldLabelClass(errorField === "offerName")}
              >
                Offer name *
              </Label>
              <Input
                id="klow-offer-name"
                value={offerName}
                onChange={(e) => {
                  setOfferName(e.target.value)
                  clearFieldError("offerName")
                }}
                placeholder="My campaign link"
                className={klowFieldInputClass(errorField === "offerName")}
                aria-invalid={errorField === "offerName"}
              />
              {renderFieldError("offerName")}
            </div>
            <div>
              <Label>Vertical</Label>
              <Select value={verticalSlug} onValueChange={setVerticalSlug}>
                <SelectTrigger className="border-[#555] bg-[#3a3a3a]">
                  <SelectValue placeholder="Select vertical" />
                </SelectTrigger>
                <SelectContent>
                  {verticalOptions.map((v) => (
                    <SelectItem key={v.slug} value={v.slug}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 border-b border-[#4a4a4a] pb-2">
            {(
              [
                ["existing", "Use existing"],
                ["generate", "New article"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => handleModeChange(key)}
                className={`rounded px-3 py-1.5 text-sm ${
                  mode === key
                    ? "bg-[#8b9aff] text-white"
                    : "bg-[#3a3a3a] text-[#b0b0b0] hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === "existing" && (
            <div
              className="space-y-2"
              ref={(el) => {
                fieldRefs.current.article = el
              }}
            >
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#888]" />
                    <Input
                      value={articleSearch}
                      onChange={(e) => setArticleSearch(e.target.value)}
                      placeholder="Search by title, slug, or keywords…"
                      className="border-[#555] bg-[#3a3a3a] pl-9"
                    />
                  </div>
                  <label
                    className="flex shrink-0 cursor-pointer items-center gap-2 rounded-md border border-[#555] bg-[#3a3a3a] px-2.5 py-2 text-xs text-[#d0d0d0]"
                    title="Show articles from all media buyers"
                  >
                    <Checkbox
                      checked={showAllArticles}
                      onCheckedChange={(checked) =>
                        setShowAllArticles(checked === true)
                      }
                      className="border-[#777] data-[state=checked]:border-[#8b9aff] data-[state=checked]:bg-[#7488e8]"
                    />
                    <span className="whitespace-nowrap">Show all</span>
                  </label>
                  {isAdmin && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    title="Sync articles from KLOW"
                    disabled={syncArticlesMutation.isPending}
                    onClick={() => {
                      void (async () => {
                        setError(null)
                        setSyncMessage(null)
                        try {
                          const result = await syncArticlesMutation.mutateAsync()
                          const imported = result.synced_count ?? 0
                          const ready = (result.data ?? []).filter(isKlowArticleReady).length
                          if (imported === 0) {
                            setSyncMessage(
                              "Sync finished but KLOW returned no articles. Try generating a new article instead.",
                            )
                          } else if (ready === 0) {
                            setSyncMessage(
                              `Imported ${imported} article(s) from KLOW — none are ready to use yet (missing slug/URL).`,
                            )
                          } else {
                            setSyncMessage(
                              `Synced ${imported} from KLOW — ${ready} ready to use.`,
                            )
                          }
                          void articlesQuery.refetch()
                        } catch (err) {
                          setError(
                            err instanceof ApiError
                              ? err.message
                              : "Failed to sync articles from KLOW",
                          )
                        }
                      })()
                    }}
                    className="shrink-0 border-[#555] bg-[#3a3a3a]"
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${syncArticlesMutation.isPending ? "animate-spin" : ""}`}
                    />
                  </Button>
                  )}
                </div>
                <div
                  className={cn(
                    "max-h-80 space-y-2 overflow-y-auto rounded border p-2",
                    errorField === "article"
                      ? "border-red-500 ring-1 ring-red-500/50"
                      : "border-[#4a4a4a]",
                  )}
                >
                {articlesQuery.isLoading && (
                  <p className="text-sm text-[#888]">Loading articles…</p>
                )}
                {!articlesQuery.isLoading && listArticles.length === 0 && (
                  <p className="text-sm text-[#888]">
                    {debouncedArticleSearch
                      ? "No articles match your search."
                      : showAllArticles
                        ? isAdmin
                          ? "No ready articles yet. Generate one or sync from KLOW."
                          : "No ready articles yet. Generate a new article to get started."
                        : "No ready articles from you yet. Generate a new article or enable Show all."}
                  </p>
                )}
                {isAdmin && syncMessage && (
                  <p className="text-xs text-[#60a5fa]">{syncMessage}</p>
                )}
                {listArticles.map((article) => {
                  const isReady = isKlowArticleReady(article)
                  const itemBaseUrl =
                    article.base_url ||
                    buildKlowArticleBaseUrl(
                      article.domain,
                      article.slug,
                      article.url,
                    )
                  return (
                  <button
                    key={article.id}
                    type="button"
                    disabled={!isReady}
                    onClick={() => {
                      if (!isReady) return
                      setSelectedArticleId(article.id)
                      setPreviewArticle(article)
                      setPollingJobId(null)
                      setPollingRequestId(null)
                      setPollingArticleId(null)
                      clearFieldError("article")
                    }}
                    className={`w-full rounded border px-3 py-2 text-left text-sm transition-colors ${
                      !isReady
                        ? "cursor-not-allowed border-[#444] bg-[#2e2e2e] opacity-80"
                        : selectedArticleId === article.id
                        ? "border-[#8b9aff] bg-[#3a3a3a]"
                        : "border-[#555] bg-[#333] hover:border-[#777]"
                    }`}
                  >
                    <div className="font-medium">
                      {article.title || article.slug || "Untitled article"}
                      {!isReady && (
                        <span className="ml-2 text-xs font-normal text-[#fbbf24]">
                          Generating…
                        </span>
                      )}
                    </div>
                    {article.slug && (
                      <div className="truncate text-xs text-[#aaa]">{article.slug}</div>
                    )}
                    {article.created_by_username && (
                      <div className="text-xs text-[#777]">
                        by {article.created_by_username}
                      </div>
                    )}
                    <div className="truncate text-xs text-[#888]">
                      {itemBaseUrl || article.final_url}
                    </div>
                  </button>
                )})}
                </div>
                {renderFieldError("article")}
            </div>
          )}

          {mode === "generate" && (
            <div className="space-y-3">
              <div
                ref={(el) => {
                  fieldRefs.current.topic = el
                }}
              >
                <Label
                  htmlFor="klow-topic"
                  className={klowFieldLabelClass(errorField === "topic")}
                >
                  Topic *
                </Label>
                <Input
                  id="klow-topic"
                  value={topic}
                  onChange={(e) => {
                    setTopic(e.target.value)
                    clearFieldError("topic")
                  }}
                  className={klowFieldInputClass(errorField === "topic")}
                  aria-invalid={errorField === "topic"}
                />
                {renderFieldError("topic")}
              </div>
              <div
                ref={(el) => {
                  fieldRefs.current.language = el
                }}
              >
                <Label
                  htmlFor="klow-language"
                  className={klowFieldLabelClass(errorField === "language")}
                >
                  Language *
                </Label>
                <Select
                  value={language}
                  onValueChange={(next) => {
                    setLanguage(next as KlowLanguageCode)
                    clearFieldError("language")
                  }}
                >
                  <SelectTrigger
                    id="klow-language"
                    className={klowFieldInputClass(errorField === "language")}
                    aria-invalid={errorField === "language"}
                  >
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent className="border-[#555] bg-[#2a2a2a]">
                    {KLOW_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {renderFieldError("language")}
              </div>
              <div>
                <Label htmlFor="klow-title">Title</Label>
                <Input
                  id="klow-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="border-[#555] bg-[#3a3a3a]"
                />
              </div>
              <div>
                <Label htmlFor="klow-keywords">Keywords (comma separated)</Label>
                <Input
                  id="klow-keywords"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  className="border-[#555] bg-[#3a3a3a]"
                />
              </div>
              <div>
                <Label htmlFor="klow-additional">Additional instructions</Label>
                <Textarea
                  id="klow-additional"
                  value={additional}
                  onChange={(e) => setAdditional(e.target.value)}
                  rows={3}
                  className="border-[#555] bg-[#3a3a3a]"
                />
              </div>
              <Button
                type="button"
                onClick={() => void handleStartGenerate()}
                disabled={isBusy}
              >
                Start generation
              </Button>
            </div>
          )}

          {mode === "existing" && selectedArticleId && articleBaseUrl && (
            <div className="space-y-3 rounded-lg border border-[#4a4a4a] bg-[#1f1f1f] p-4">
              <h3 className="text-sm font-semibold text-white">
                RSOC feed URL settings
              </h3>
              <p className="text-xs leading-snug text-[#a0a0a0]">
                Paste the final URL as your TikTok ad landing page.{" "}
                <span className="font-mono text-[#c8c8c8]">atxt</span> is included
                in the link (fill later in TikTok if needed);{" "}
                <span className="font-mono text-[#c8c8c8]">ct</span> is the lander
                template (must be crawled by AdSense before launch).
              </p>

              <div>
                <Label htmlFor="klow-ad-copy">Ad copy text (atxt)</Label>
                <Textarea
                  id="klow-ad-copy"
                  value={adCopyText}
                  onChange={(e) => setAdCopyText(e.target.value)}
                  rows={2}
                  placeholder="Optional — leave blank to set in TikTok later"
                  className="border-[#555] bg-[#3a3a3a]"
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div
                  ref={(el) => {
                    fieldRefs.current.rsocTemplateId = el
                  }}
                >
                  <Label className={klowFieldLabelClass(errorField === "rsocTemplateId")}>
                    Article template (ct) *
                  </Label>
                  <Select
                    value={rsocTemplateId || undefined}
                    onValueChange={(value) => {
                      setRsocTemplateId(value)
                      clearFieldError("rsocTemplateId")
                    }}
                  >
                    <SelectTrigger
                      className={klowFieldInputClass(errorField === "rsocTemplateId")}
                      aria-invalid={errorField === "rsocTemplateId"}
                    >
                      <SelectValue placeholder="Pick layout template" />
                    </SelectTrigger>
                    <SelectContent
                      className="border-[#555] bg-[#2a2a2a]"
                      position="popper"
                      sideOffset={4}
                      collisionPadding={16}
                    >
                      {rsocTemplateOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {renderFieldError("rsocTemplateId")}
                </div>
                <div
                  ref={(el) => {
                    fieldRefs.current.googleTemplateId = el
                  }}
                >
                  <Label className={klowFieldLabelClass(errorField === "googleTemplateId")}>
                    KW template (stid) *
                  </Label>
                  <Select
                    value={googleTemplateId || undefined}
                    onValueChange={(value) => {
                      setGoogleTemplateId(value)
                      clearFieldError("googleTemplateId")
                    }}
                  >
                    <SelectTrigger
                      className={klowFieldInputClass(errorField === "googleTemplateId")}
                      aria-invalid={errorField === "googleTemplateId"}
                    >
                      <SelectValue placeholder="Pick SERP style" />
                    </SelectTrigger>
                    <SelectContent
                      className="border-[#555] bg-[#2a2a2a]"
                      position="popper"
                      sideOffset={4}
                      collisionPadding={16}
                    >
                      {googleTemplateOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {renderFieldError("googleTemplateId")}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="klow-sqs">Suggested keywords (sqs)</Label>
                  <Input
                    id="klow-sqs"
                    value={sqsKeywords}
                    onChange={(e) => setSqsKeywords(e.target.value)}
                    placeholder="keyword1,keyword2,keyword3"
                    className="border-[#555] bg-[#3a3a3a]"
                  />
                </div>
                <div
                  ref={(el) => {
                    fieldRefs.current.pixelId = el
                  }}
                >
                  <Label className={klowFieldLabelClass(errorField === "pixelId")}>
                    Pixel (spid) *
                  </Label>
                  <Select
                    value={pixelId || undefined}
                    onValueChange={(value) => {
                      setPixelId(value)
                      clearFieldError("pixelId")
                    }}
                    disabled={pixelsQuery.isLoading}
                  >
                    <SelectTrigger
                      className={klowFieldInputClass(errorField === "pixelId")}
                      aria-invalid={errorField === "pixelId"}
                    >
                      <SelectValue
                        placeholder={
                          pixelsQuery.isLoading
                            ? "Loading pixels…"
                            : pixelsQuery.isError
                              ? "Failed to load pixels"
                              : pixelSelectOptions.length === 0
                                ? "No pixels assigned to you"
                                : "Select pixel"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent
                      className="border-[#555] bg-[#2a2a2a]"
                      position="popper"
                      sideOffset={4}
                      collisionPadding={16}
                    >
                      {pixelSelectOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {renderFieldError("pixelId")}
                  {pixelsQuery.isError && !errorField && (
                    <div className="mt-1 space-y-2">
                      <p className="text-xs text-red-400">
                        {pixelsLoadErrorMessage ??
                          "Could not load your assigned pixels. Refresh the page and try again."}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 border-[#555] bg-transparent text-xs text-white hover:bg-[#444]"
                        onClick={() => void pixelsQuery.refetch()}
                        disabled={pixelsQuery.isFetching}
                      >
                        {pixelsQuery.isFetching ? "Retrying…" : "Retry"}
                      </Button>
                    </div>
                  )}
                  {!pixelsQuery.isLoading &&
                    !pixelsQuery.isError &&
                    pixelSelectOptions.length === 0 && (
                      <p className="mt-1 text-xs text-[#a0a0a0]">
                        No pixel is assigned to your account ({user?.username ?? "current user"}).
                        Ask an admin to assign one in Admin → Pixels.
                      </p>
                    )}
                </div>
              </div>

              {previewFeedUrl && (
                <div>
                  <Label className="text-[#e0e0e0]">Landing page URL preview</Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      readOnly
                      value={previewFeedUrl}
                      className="font-mono text-xs bg-[#101010] text-[#e0e0e0]"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 border-[#555] bg-[#3a3a3a]"
                      disabled={!previewFeedUrl}
                      onClick={() =>
                        window.open(previewFeedUrl, "_blank", "noopener,noreferrer")
                      }
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Preview
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {statusMessage && (
            <p className="text-sm text-[#60a5fa]">{statusMessage}</p>
          )}
          {error && !errorField && (
            <p className="text-sm text-red-400">{error}</p>
          )}
        </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleCreateOffer()}
            disabled={isBusy || !canCreateOffer}
          >
            {createOfferMutation.isPending ? "Creating…" : "Create offer & copy URL"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
